import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export type LandingVariant = 'a' | 'b' | 'c';

const STORAGE_KEY = 'danny_landing_variant';
const VALID_VARIANTS: LandingVariant[] = ['a', 'b', 'c'];

function isValidVariant(v: string | null): v is LandingVariant {
  return v !== null && VALID_VARIANTS.includes(v as LandingVariant);
}

/**
 * Determines which landing page variant to show.
 *
 * Priority:
 * 1. URL query param ?v=a|b|c (from ads, Product Hunt, referral links)
 * 2. localStorage (returning visitor sees same variant)
 * 3. Random assignment (new organic visitor)
 *
 * The resolved variant is always persisted to localStorage.
 */
export function useLandingVariant(): LandingVariant {
  const [searchParams] = useSearchParams();

  return useMemo(() => {
    // 1. Check URL param
    const paramVariant = searchParams.get('v');
    if (isValidVariant(paramVariant)) {
      localStorage.setItem(STORAGE_KEY, paramVariant);
      return paramVariant;
    }

    // 2. Check localStorage
    const storedVariant = localStorage.getItem(STORAGE_KEY);
    if (isValidVariant(storedVariant)) {
      return storedVariant;
    }

    // 3. Random assignment
    const randomVariant = VALID_VARIANTS[Math.floor(Math.random() * VALID_VARIANTS.length)];
    localStorage.setItem(STORAGE_KEY, randomVariant);
    return randomVariant;
  }, [searchParams]);
}
